import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { inventoryApi } from '../../services/inventoryApi.js';

/**
 * Edit an existing product. SKU and unit are identity-like and stay fixed;
 * the type can change - the backend rejects converting to a service while
 * stock is still on hand, and forces the stock-only fields off on conversion.
 */
export function ProductEditModal({ product, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const open = Boolean(product);
  const [form, setForm] = useState({ productType: 'GOOD', name: '', listPrice: '0', reorderPoint: 0, tracking: 'NONE', isActive: true });
  const [error, setError] = useState(null);

  useEffect(() => {
    if (product) {
      setForm({
        productType: product.productType || 'GOOD',
        name: product.name || '',
        listPrice: String(Number(product.listPrice) || 0),
        reorderPoint: Number(product.reorderPoint) || 0,
        tracking: product.tracking || 'NONE',
        isActive: product.isActive !== false,
      });
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id]);

  const isService = form.productType === 'SERVICE';

  const save = useMutation({
    mutationFn: (body) => inventoryApi.updateProduct(product.id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory', 'products'] });
      setError(null);
      onClose();
    },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  function handleField(key) {
    return (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }));
  }

  function handleSubmit(event) {
    event.preventDefault();
    setError(null);
    save.mutate({
      productType: form.productType,
      name: form.name.trim(),
      listPrice: form.listPrice || '0',
      reorderPoint: isService ? 0 : Number(form.reorderPoint) || 0,
      tracking: isService ? 'NONE' : form.tracking,
      isActive: form.isActive,
    });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={product ? `${t('inv.products.modal.editTitle')} · ${product.sku}` : t('inv.products.modal.editTitle')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={save.isPending}>
            {save.isPending ? `${t('common.save')}…` : t('common.save')}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="modal-body" style={{ padding: 0, gap: 'var(--space-3)' }}>
        <label className="field">
          <span className="field-label">{t('inv.products.modal.type')}</span>
          <select className="select" value={form.productType} onChange={handleField('productType')}>
            <option value="GOOD">{t('inv.products.modal.typeGood')}</option>
            <option value="SERVICE">{t('inv.products.modal.typeService')}</option>
          </select>
        </label>
        <label className="field">
          <span className="field-label">{t('col.name')}</span>
          <input className="input" value={form.name} onChange={handleField('name')} required />
        </label>
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('inv.products.modal.listPrice')}</span>
            <input className="input" type="number" min="0" step="0.01" value={form.listPrice} onChange={handleField('listPrice')} />
          </label>
          {!isService && (
            <label className="field">
              <span className="field-label">{t('inv.products.modal.reorderPoint')}</span>
              <input className="input" type="number" min="0" step="1" value={form.reorderPoint} onChange={handleField('reorderPoint')} />
            </label>
          )}
        </div>
        {!isService && (
          <label className="field">
            <span className="field-label">{t('inv.track.tracking')}</span>
            <select className="input" value={form.tracking} onChange={handleField('tracking')}>
              <option value="NONE">{t('inv.track.NONE')}</option>
              <option value="BATCH">{t('inv.track.BATCH')}</option>
              <option value="SERIAL">{t('inv.track.SERIAL')}</option>
            </select>
          </label>
        )}
        {isService && <p className="muted text-sm" style={{ margin: 0 }}>{t('inv.products.modal.serviceHint')}</p>}
        <label className="field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <input
            type="checkbox"
            checked={form.isActive}
            onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
          />
          <span>{t('inv.products.modal.active')}</span>
        </label>
        {error && <div className="alert danger">{error}</div>}
      </form>
    </Modal>
  );
}
